import { AddOutlined } from '@mui/icons-material'
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material'
import { type FC, useRef } from 'react'
import { useQueryLabels } from 'src/features/labelMaster/api/useQueryLabels'
import { useMultipleLabelUpload } from './useMultipleLabelUpload'

const MAX_LABEL_COUNT = 5

type Props = {
  labelMasterId: string
  onSingleLabelAdded: (labelId: string) => void
}

export const LabelAddButton: FC<Props> = ({
  labelMasterId,
  onSingleLabelAdded,
}) => {
  const { data: labels, isLoading } = useQueryLabels(labelMasterId)
  const count = labels?.length ?? 0

  const { uploadMultipleLabels, isUploading } = useMultipleLabelUpload({
    labelMasterId,
    maxCount: MAX_LABEL_COUNT,
    count,
    onSingleLabelAdded,
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleUpload = async () => {
    if (!fileInputRef.current) return

    const files = fileInputRef.current.files
    if (!files || files.length === 0) return

    await uploadMultipleLabels(files)
    fileInputRef.current.value = ''
  }

  const canAdd = count < MAX_LABEL_COUNT

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        size="medium"
        startIcon={<AddOutlined />}
        onClick={() => fileInputRef.current?.click()}
        disabled={isLoading || isUploading || !canAdd}
        sx={{ width: 'fit-content' }}
      >
        ラベルを追加 ({count}/{MAX_LABEL_COUNT})
        <input
          type="file"
          hidden
          ref={fileInputRef}
          onChange={handleUpload}
          accept="image/*"
          multiple
        />
      </Button>

      <Backdrop
        sx={{
          color: 'common.white',
          zIndex: theme => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
        }}
        open={isUploading}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress color="primary" />
          <Typography variant="body1" color="text.primary">
            画像をアップロード中...
          </Typography>
        </Box>
      </Backdrop>
    </>
  )
}
